import { PATHS } from '@/config/paths';
import type { MenuPermissionsMap, User } from '@/features/auth/types/auth.types';
import type { MenuTreeNode } from '@/features/menus/types/menu.types';
import { extractOpsMenuTree, isDetachedOpsRoutePath } from '@/features/menus/utils/menuTreeFilters';

import { canAccessOpsNavItem } from './filterOpsNav';
import { getOpsNavItemsForMenu } from './opsNav.config';
import type { OpsNavItem } from './opsNav.types';

/**
 * Chuẩn hóa path shell vận hành: `/admin/...` → `/ops/...` (cùng quy ước {@link LegacyAdminToOpsRedirect}).
 */
export function normalizeOpsShellPath(path: string): string {
  const p = path.trim();
  if (p.startsWith('http')) return p;
  if (p.startsWith('/admin')) return p.replace(/^\/admin(?=\/|$)/, PATHS.OPS);
  return p;
}

/** Path menu BE → path route đã đăng ký trên FE (tránh 404 khi slug khác). */
const OPS_BE_PATH_TO_CANONICAL: Record<string, string> = {
  [PATHS.OPS_AUDIT_LOG_ALIAS]: PATHS.OPS_AUDIT,
  [PATHS.OPS_NETCDF_DATA_ALIAS]: PATHS.DATA_UPLOAD,
  [PATHS.OPS_NETCDF_LEGACY]: PATHS.DATA_UPLOAD,
};

/**
 * Map path từ menu BE sang path có route trong {@link src/app/router.tsx}.
 */
export function canonicalOpsRoutePath(path: string): string {
  const shell = normalizeOpsShellPath(path);
  return OPS_BE_PATH_TO_CANONICAL[shell] ?? shell;
}

/** Path lá ẩn khỏi sidebar Ops (vẫn có route + RequireOpsRoute). */
const OPS_SIDEBAR_HIDDEN_PATHS = new Set<string>([PATHS.OPS_MENUS]);

/**
 * Bỏ các node trùng {@link OPS_SIDEBAR_HIDDEN_PATHS} (sau chuẩn hóa path) khỏi cây menu động.
 */
export function filterOpsSidebarHiddenPaths(tree: MenuTreeNode[]): MenuTreeNode[] {
  const walk = (nodes: MenuTreeNode[]): MenuTreeNode[] => {
    const out: MenuTreeNode[] = [];
    for (const n of nodes) {
      if (!n.is_active) continue;
      const raw = n.path?.trim();
      const path = raw ? canonicalOpsRoutePath(normalizeOpsShellPath(raw)) : '';
      if (path && OPS_SIDEBAR_HIDDEN_PATHS.has(path)) continue;
      const children = walk(n.children ?? []);
      out.push({ ...n, children });
    }
    return out.sort((a, b) => a.sort_order - b.sort_order);
  };
  return walk(tree);
}

function collectLeafPathsFromTree(nodes: MenuTreeNode[], out: string[]): void {
  const sorted = [...nodes].filter((n) => n.is_active).sort((a, b) => a.sort_order - b.sort_order);
  for (const n of sorted) {
    const children = (n.children ?? []).filter((c) => c.is_active);
    if (children.length > 0) {
      collectLeafPathsFromTree(children, out);
    } else if (n.path && typeof n.path === 'string') {
      const raw = n.path.trim();
      if (!raw.startsWith('http')) {
        const p = canonicalOpsRoutePath(raw);
        const underOps = p === PATHS.OPS || p.startsWith(`${PATHS.OPS}/`);
        if (underOps || isDetachedOpsRoutePath(p)) {
          out.push(p);
        }
      }
    }
  }
}

/**
 * Các path lá (link thật) trong nhánh Vận hành từ `GET /api/menus/me`, sau khi gắn với cây ops.
 */
export function getDynamicOpsLeafPaths(menuTree: MenuTreeNode[] | undefined): string[] {
  if (!menuTree?.length) return [];
  const opsRoots = extractOpsMenuTree(menuTree);
  const out: string[] = [];
  collectLeafPathsFromTree(opsRoots, out);
  return [...new Set(out)];
}

/**
 * Thứ tự: theo {@link getOpsNavItemsForMenu}; chỉ gồm path đã đăng ký trên FE (có route).
 * Quyền: rule tĩnh **hoặc** path xuất hiện trên menu BE (leaf).
 */
export function getMergedAccessibleOpsPaths(
  user: User | null,
  menuPermissions: MenuPermissionsMap | null | undefined,
  menuTree: MenuTreeNode[] | undefined,
): string[] {
  const dynamicPaths = getDynamicOpsLeafPaths(menuTree);
  const ordered: string[] = [];
  const seen = new Set<string>();

  for (const n of getOpsNavItemsForMenu()) {
    const canStatic = canAccessOpsNavItem(user, n, menuPermissions);
    const prefix = `${n.path}/`;
    /** Nav cha hợp lệ khi bản thân là leaf, hoặc có ít nhất một leaf con trong cây BE. */
    const canDynamic =
      dynamicPaths.includes(n.path) || dynamicPaths.some((p) => p.startsWith(prefix));
    if ((canStatic || canDynamic) && !seen.has(n.path)) {
      ordered.push(n.path);
      seen.add(n.path);
    }
  }
  return ordered;
}

export function getMergedAccessibleOpsNavItems(
  user: User | null,
  menuPermissions: MenuPermissionsMap | null | undefined,
  menuTree: MenuTreeNode[] | undefined,
): OpsNavItem[] {
  const paths = getMergedAccessibleOpsPaths(user, menuPermissions, menuTree);
  const byPath = new Map(getOpsNavItemsForMenu().map((i) => [i.path, i]));
  return paths.map((p) => byPath.get(p)).filter((x): x is OpsNavItem => Boolean(x));
}

/** Mục đầu tiên user vào được từ `/ops` (redirect index). */
export function getFirstMergedAccessibleOpsPath(
  user: User | null,
  menuPermissions: MenuPermissionsMap | null | undefined,
  menuTree: MenuTreeNode[] | undefined,
): string | null {
  const paths = getMergedAccessibleOpsPaths(user, menuPermissions, menuTree);
  return paths[0] ?? null;
}

/**
 * Path lá đầu tiên trong nhánh Vận hành — **chỉ** từ `GET /api/menus/me` (thứ tự sort BE).
 */
export function getFirstApiOpsLeafPath(menuTree: MenuTreeNode[] | undefined): string | null {
  const paths = getDynamicOpsLeafPaths(menuTree);
  return paths[0] ?? null;
}
